import { useState } from 'react';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { logPhotoShareConsent } from '@busy/api';
import { useT } from '../lib/i18n';

// Modal shown when a barber taps a photo tile in a client's gallery. Full-size
// preview + a Share button that opens the OS share sheet (text / email / IG /
// Messages / etc.) via the Web Share API's files variant. Falls back to a
// "Save image" hint when files-share isn't supported.
//
// The fetch-to-blob step lets us hand a real File to navigator.share, which
// is what most OS share sheets actually need to route the image to social
// apps. A signed URL alone often gets rejected by the share targets.

export function SharePhotoModal({
  imageUrl,
  clientName,
  clientId,
  shareTarget = 'gallery',
  onClose,
}: {
  imageUrl: string;
  clientName: string;
  /** Client the photo belongs to. Optional because the after-cut composite
   *  share path captures consent before the visit row is committed (the
   *  client_id may exist or may be null in some flows). Logged either way. */
  clientId?: string | null;
  /** Where the modal was opened from — 'gallery' (client detail photo grid)
   *  or 'after_cut_step' (composite share inside the cut flow). Recorded on
   *  the consent log row so the audit trail knows the surface. */
  shareTarget?: 'gallery' | 'after_cut_step';
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);

  async function handleShare() {
    if (!consent) return;
    setBusy(true);
    // Log the affirmation immediately — fire-and-forget. We don't await it
    // because the share sheet should open as fast as possible after the tap;
    // the log row is purely an audit artifact.
    void logPhotoShareConsent({
      clientId: clientId ?? null,
      clientName,
      photoUrl: imageUrl,
      shareTarget,
    });
    try {
      const blob = await (await fetch(imageUrl)).blob();
      const stamp = new Date().toISOString().slice(0, 10);
      // Filename is name-free — recipients see it on email/MMS/IG, and
      // the client's name should never leak through any outgoing payload.
      // Same rule as title + text below.
      const filename = `cut-${stamp}.jpg`;
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });

      const nav = typeof navigator !== 'undefined' ? (navigator as Navigator) : null;
      if (nav?.share) {
        const payload: ShareData = {
          title: t('photoShare.title'),
          text: t('photoShare.text'),
        };
        if (nav.canShare && nav.canShare({ files: [file] })) {
          payload.files = [file];
        }
        try {
          await nav.share(payload);
          onClose();
          return;
        } catch {
          // user cancelled the share sheet — keep the modal open so they can retry
          setBusy(false);
          return;
        }
      }
      // No Web Share API — best we can do on desktop is copy the URL.
      try {
        await navigator.clipboard.writeText(imageUrl);
        toast(t('photoShare.urlCopied'), 'success');
      } catch {
        toast(t('photoShare.unsupported'), 'error');
      }
    } catch (e) {
      toast(t('photoShare.fetchFail', { message: e instanceof Error ? e.message : '' }), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell open onClose={busy ? () => undefined : onClose} size="md" ariaLabel={t('photoShare.aria', { name: clientName })}>
      <div className="bg-navy-900 text-white px-6 pt-6 pb-4 rounded-t-2xl relative">
        {!busy && <ModalCloseButton onClose={onClose} variant="dark" />}
        <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-gold-400 mb-1.5">
          {t('photoShare.eyebrow')}
        </div>
        <h2 className="font-display text-2xl leading-tight">{clientName}</h2>
      </div>

      <div className="bg-bg-alt p-4">
        <div className="rounded-xl overflow-hidden bg-black">
          <img src={imageUrl} alt={clientName} className="w-full max-h-[60dvh] object-contain" />
        </div>

        {/* Permission warning — explains the responsibility. The checkbox
            below captures the actual affirmation, which is logged on share. */}
        <div className="mt-4 rounded-xl border-2 border-red-500/60 bg-red-50 dark:bg-red-600/15 p-3 flex items-start gap-3">
          <AlertIcon className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <strong className="block text-sm text-red-900 dark:text-red-200">
              {t('photoShare.warnTitle')}
            </strong>
            <p className="text-xs text-red-900/80 dark:text-red-200/80 leading-relaxed mt-0.5">
              {t('photoShare.warnBody', { name: clientName })}
            </p>
          </div>
        </div>

        {/* Affirmative consent checkbox — gates the Share button below.
            Tick state is logged to photo_share_consent_logs at share time so
            we have a paper trail if a client later disputes authorization. */}
        <label className="mt-3 flex items-start gap-3 rounded-xl border border-line bg-surface p-3 cursor-pointer hover:border-navy-900/40 dark:hover:border-gold-500/40 transition-colors">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            disabled={busy}
            className="mt-0.5 w-5 h-5 shrink-0 accent-navy-900 dark:accent-gold-500 cursor-pointer"
          />
          <span className="text-sm text-ink-900 leading-snug">
            {t('photoShare.consentCheckbox')}
          </span>
        </label>
      </div>

      <div className="border-t border-line p-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="flex-1 h-11 rounded-lg border border-line bg-surface font-semibold disabled:opacity-50"
        >
          {t('photoShare.cancel')}
        </button>
        <button
          type="button"
          onClick={handleShare}
          disabled={busy || !consent}
          aria-disabled={!consent}
          title={!consent ? t('photoShare.shareDisabledHint') : undefined}
          className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ShareIcon className="w-4 h-4" />
          {busy ? t('photoShare.preparing') : t('photoShare.share')}
        </button>
      </div>
    </ModalShell>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
